/**
 * Web Socket Manager
 * Manages all socket connections with health monitoring, automatic reconnection,
 * and token refresh integration
 */
class SocketManager {
  constructor() {
    if (SocketManager.instance) {
      return SocketManager.instance;
    }

    this.sockets = new Map(); // namespace -> socket service instance
    this.connectionStates = new Map(); // namespace -> connection state
    this.healthCheckIntervals = new Map(); // namespace -> interval ID
    this.reconnectTimeouts = new Map(); // namespace -> timeout ID
    this.isOnline = true;
    this.tokenRefreshInProgress = false;
    
    // Configuration
    this.config = {
      healthCheckInterval: 30000, // 30 seconds
      maxReconnectAttempts: 10,
      baseReconnectDelay: 1000, // 1 second
      maxReconnectDelay: 30000, // 30 seconds
      connectionTimeout: 20000, // 20 seconds
    };

    this.setupListeners();
    SocketManager.instance = this;
  }

  static getInstance() {
    if (!SocketManager.instance) {
      SocketManager.instance = new SocketManager();
    }
    return SocketManager.instance;
  }

  setupListeners() {
    // Monitor network connectivity using browser APIs
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.handleOnline();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.handleOffline();
    });

    // Handle page visibility
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.handleForeground();
      } else {
        this.handleBackground();
      }
    });
  }

  /**
   * Register a socket service with the manager
   */
  registerSocket(namespace, socketService) {
    if (this.sockets.has(namespace)) {
      console.warn(`⚠️ Socket already registered for namespace: ${namespace}`);
      return;
    }

    this.sockets.set(namespace, socketService);
    this.connectionStates.set(namespace, {
      isConnected: false,
      reconnectAttempts: 0,
      lastConnectedAt: null,
      lastDisconnectedAt: null,
      lastError: null,
    });

    // Start health check for this socket
    this.startHealthCheck(namespace);
  }

  /**
   * Unregister a socket service
   */
  unregisterSocket(namespace) {
    
    this.stopHealthCheck(namespace);
    this.clearReconnectTimeout(namespace);
    this.sockets.delete(namespace);
    this.connectionStates.delete(namespace);
  }

  /**
   * Get a registered socket service
   */
  getSocket(namespace) {
    return this.sockets.get(namespace);
  }

  /**
   * Get connection state for a namespace
   */
  getConnectionState(namespace) {
    return this.connectionStates.get(namespace);
  }

  /**
   * Update connection state
   */
  updateConnectionState(namespace, updates) {
    const currentState = this.connectionStates.get(namespace) || {};
    this.connectionStates.set(namespace, {
      ...currentState,
      ...updates,
    });
  }

  /**
   * Start health check for a socket
   */
  startHealthCheck(namespace) {
    // Clear any existing health check
    this.stopHealthCheck(namespace);

    const intervalId = setInterval(() => {
      const socket = this.sockets.get(namespace);
      if (!socket || !socket.isConnected) return;

      // SocketService.emit does not support Socket.IO acks, so avoid ping-with-ack.
      // Instead, treat the connection as healthy as long as the underlying socket is connected.
      const isConnected = socket.isConnected && socket.socket?.connected;
      if (!isConnected) {
        console.warn(`⚠️ Health check detected disconnection for ${namespace}`);
        this.handleUnexpectedDisconnection(namespace);
      }
    }, this.config.healthCheckInterval);

    this.healthCheckIntervals.set(namespace, intervalId);
  }

  /**
   * Stop health check for a socket
   */
  stopHealthCheck(namespace) {
    const intervalId = this.healthCheckIntervals.get(namespace);
    if (intervalId) {
      clearInterval(intervalId);
      this.healthCheckIntervals.delete(namespace);
    }
  }

  /**
   * Handle unexpected disconnection
   */
  handleUnexpectedDisconnection(namespace) {
    const state = this.connectionStates.get(namespace);
    if (!state) return;

    this.updateConnectionState(namespace, {
      isConnected: false,
      lastDisconnectedAt: new Date(),
    });

    this.scheduleReconnect(namespace);
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  scheduleReconnect(namespace) {
    const state = this.connectionStates.get(namespace);
    if (!state) return;

    // Clear any existing reconnect timeout
    this.clearReconnectTimeout(namespace);

    const { reconnectAttempts } = state;
    if (reconnectAttempts >= this.config.maxReconnectAttempts) {
      console.error(`❌ Max reconnection attempts reached for ${namespace}`);
      return;
    }

    // Calculate delay with exponential backoff
    const delay = Math.min(
      this.config.baseReconnectDelay * Math.pow(2, reconnectAttempts),
      this.config.maxReconnectDelay
    );


    const timeoutId = setTimeout(() => {
      this.reconnect(namespace);
    }, delay);

    this.reconnectTimeouts.set(namespace, timeoutId);
  }

  /**
   * Clear reconnect timeout
   */
  clearReconnectTimeout(namespace) {
    const timeoutId = this.reconnectTimeouts.get(namespace);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.reconnectTimeouts.delete(namespace);
    }
  }

  /**
   * Reconnect a specific socket
   */
  async reconnect(namespace) {
    const socket = this.sockets.get(namespace);
    const state = this.connectionStates.get(namespace);
    if (!socket || !state) return;


    try {
      // Check if token refresh is needed
      await this.refreshTokenIfNeeded();

      // Attempt reconnection
      await socket.connect();

      // Update state on successful reconnection
      this.updateConnectionState(namespace, {
        isConnected: true,
        lastConnectedAt: new Date(),
        reconnectAttempts: 0,
        lastError: null,
      });


    } catch (error) {
      console.error(`❌ Failed to reconnect ${namespace}:`, error);

      // Update state and schedule next attempt
      this.updateConnectionState(namespace, {
        isConnected: false,
        reconnectAttempts: state.reconnectAttempts + 1,
        lastError: error,
      });

      this.scheduleReconnect(namespace);
    }
  }

  /**
   * Reconnect all registered sockets
   */
  async reconnectAll() {

    const reconnectPromises = Array.from(this.sockets.keys()).map(namespace => 
      this.reconnect(namespace)
    );

    try {
      await Promise.all(reconnectPromises);
    } catch (error) {
      console.error('❌ Failed to reconnect all sockets:', error);
    }
  }

  /**
   * Handle online event
   */
  async handleOnline() {
    await this.reconnectAll();
  }

  /**
   * Handle offline event
   */
  handleOffline() {
    this.pauseAllReconnections();
  }

  /**
   * Handle app coming to foreground
   */
  async handleForeground() {
    
    if (this.isOnline) {
      // Check all connections and reconnect if needed
      for (const [namespace] of this.sockets.entries()) {
        const state = this.connectionStates.get(namespace);
        if (!state?.isConnected) {
          await this.reconnect(namespace);
        }
      }
    }
  }

  /**
   * Handle app going to background
   */
  handleBackground() {
    // Optionally disconnect non-critical sockets
  }

  /**
   * Pause all reconnection attempts
   */
  pauseAllReconnections() {
    
    for (const namespace of this.sockets.keys()) {
      this.clearReconnectTimeout(namespace);
      this.stopHealthCheck(namespace);
    }
  }

  /**
   * Refresh token if needed
   */
  async refreshTokenIfNeeded() {
    // Implement token refresh logic here if needed
    return true;
  }

  /**
   * Disconnect all sockets
   */
  async disconnectAll() {

    // Stop all health checks and reconnection attempts
    this.pauseAllReconnections();

    // Disconnect each socket
    const disconnectPromises = Array.from(this.sockets.entries()).map(
      async ([namespace, socket]) => {
        try {
          await socket.disconnect();
        } catch (error) {
          console.error(`❌ Failed to disconnect ${namespace}:`, error);
        }
      }
    );

    await Promise.all(disconnectPromises);
  }

  /**
   * Get status of all sockets
   */
  getStatus() {
    const status = {};
    
    for (const [namespace, state] of this.connectionStates.entries()) {
      status[namespace] = {
        ...state,
        healthCheckActive: this.healthCheckIntervals.has(namespace),
        reconnectPending: this.reconnectTimeouts.has(namespace),
      };
    }

    return {
      isOnline: this.isOnline,
      sockets: status,
    };
  }

  /**
   * Cleanup on app termination
   */
  async cleanup() {
    // Remove event listeners
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    document.removeEventListener('visibilitychange', () => {});

    // Disconnect all sockets
    await this.disconnectAll();

    // Clear all intervals and timeouts
    for (const namespace of this.sockets.keys()) {
      this.stopHealthCheck(namespace);
      this.clearReconnectTimeout(namespace);
    }

    // Clear maps
    this.sockets.clear();
    this.connectionStates.clear();
    this.healthCheckIntervals.clear();
    this.reconnectTimeouts.clear();
  }
}

export default SocketManager;